/*! Copyright (c) Safe As Milk. All rights reserved. */
import { Loader } from "googlemaps-js-api-loader";

import { getPinStyle, getMapStyle } from "map-settings";

class InteractiveMap extends HTMLElement {
    #apiKey;
    #started = false;
    #observer = null;
    /* BasKas (issue #614): kortet indlæses først når sektionen er ved at komme
       i syne. Maps JavaScript API er meteret — 10.000 kortindlæsninger gratis
       pr. måned, derefter $7/1.000 — og de fleste besøgende scroller aldrig ned
       til kort-sektionen. Før dette betalte vi for hver eneste sidevisning.
       Adfærden efter indlæsning er uændret. Kan browseren ikke
       IntersectionObserver, indlæses kortet med det samme som før. */
    connectedCallback() {
        if (this.#started) return;
        if (typeof IntersectionObserver !== "function") {
            this.#render();
            return;
        }
        this.#observer = new IntersectionObserver((entries, observer) => {
            if (entries.some(entry => entry.isIntersecting)) {
                observer.disconnect();
                this.#observer = null;
                this.#render();
            }
        }, {
            rootMargin: "200px"
        });
        this.#observer.observe(this);
    }
    disconnectedCallback() {
        if (this.#observer) {
            this.#observer.disconnect();
            this.#observer = null;
        }
    }
    async #render() {
        if (this.#started) return;
        this.#started = true;
        this.#apiKey = this.getAttribute("api-key");
        if (!this.#apiKey) return;
        this.map = null;
        this.address = this.getAttribute("map-address");
        this.mapStyle = this.getAttribute("map-style");
        this.mapPinStyle = this.getAttribute("map-pin-style");
        try {
            if (!window?.google?.maps) {
                const loader = new Loader({
                    apiKey: this.#apiKey,
                    version: "weekly",
                    libraries: [ "core", "geocoding", "maps", "marker" ]
                });
                await loader.importLibrary("core");
            }
            const {Map: Map} = await window.google.maps.importLibrary("maps");
            const options = {
                zoom: 14,
                zoomControl: true,
                center: {
                    lat: 0,
                    lng: 0
                },
                disableDefaultUI: true,
                scrollwheel: false,
                keyboardShortcuts: false,
                styles: getMapStyle(this.mapStyle)
            };
            this.map = new Map(this, options);
            /* BasKas: hvis map-address allerede er "lat,lng", så brug punktet
               direkte i stedet for at geokode. Google's geocoder snapper et
               koordinat til nærmeste adresse (målt til ~80 m forkert ved
               Smukfest-pladsen), og en adresse rammer bygningen — ikke pletten
               hvor vi holder. Koordinater skal lande præcist. */
            const latLng = (this.address || "").trim().match(/^(-?\d{1,3}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)$/);
            if (latLng) {
                const position = {
                    lat: parseFloat(latLng[1]),
                    lng: parseFloat(latLng[2])
                };
                this.map.setCenter(position);
                this.map.setZoom(16);
                new window.google.maps.Marker({
                    map: this.map,
                    position: position,
                    icon: getPinStyle(this.mapPinStyle)
                });
                return;
            }
            const {Geocoder: Geocoder} = await window.google.maps.importLibrary("geocoding");
            const geocoder = new Geocoder;
            geocoder.geocode({
                address: this.address
            }, ((results, status) => {
                if (status === window.google.maps.GeocoderStatus.OK) {
                    if (status !== window.google.maps.GeocoderStatus.ZERO_RESULTS) {
                        this.map.setCenter(results[0].geometry.location);
                        new window.google.maps.Marker({
                            map: this.map,
                            position: results[0].geometry.location,
                            icon: getPinStyle(this.mapPinStyle)
                        });
                    }
                } else {
                    console.log(`Geocode was not successful for the following reason: ${status}`);
                }
            }));
        } catch (error) {
            console.log(error);
        }
    }
}

customElements.define("interactive-map", InteractiveMap);
//# sourceMappingURL=interactive-map.js.map
